import React, { useRef, useMemo, useState, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Html } from "@react-three/drei";

/**
 * Hero Robot - a sleek, futuristic robot built with Three.js primitives.
 * Reacts to mouse position and has cinematic floating animations.
 */
const EYE_GLOW_INTENSITY = 1.2;

const DataStreams = ({ isHovered }) => {
    const pointsRef = useRef();
    const count = 40;

    const positions = useMemo(() => {
        const pos = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            pos[i * 3] = (Math.random() - 0.5) * 4;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 4;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 4;
        }
        return pos;
    }, []);

    useFrame((state) => {
        if (!pointsRef.current || !isHovered) return;
        const t = state.clock.elapsedTime;
        const array = pointsRef.current.geometry.attributes.position.array;

        for (let i = 0; i < count; i++) {
            array[i * 3 + 1] += Math.sin(t + i) * 0.01;
            array[i * 3] += Math.cos(t + i) * 0.005;
        }
        pointsRef.current.geometry.attributes.position.needsUpdate = true;
        pointsRef.current.rotation.y = t * 0.1;
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={count}
                    array={positions}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial
                size={0.04}
                color="#22d3ee"
                transparent
                opacity={isHovered ? 0.6 : 0}
                sizeAttenuation
            />
        </points>
    );
};

const OrbitingTerminal = ({ mode }) => {
    const groupRef = useRef();
    const [logs, setLogs] = useState(["BOOTING_KERNEL...", "NEURAL_LINK_ESTABLISHED", "SCANNING_ENVIRONMENT..."]);
    const [isDragging, setIsDragging] = useState(false);
    const dragPos = useRef({ x: 0, z: 0 });
    const orbitOffset = useRef(0);

    useEffect(() => {
        const interval = setInterval(() => {
            const systemLogs = [
                "OPTIMIZING_FLIGHT_PATH", "ANALYIZING_USER_GESTRUE", "SYNCING_CLOUDS",
                "UPGRADING_CORE...", "MEM_STABLE", "THREAT_SCAN: ZERO"
            ];
            setLogs(prev => [...prev.slice(-4), systemLogs[Math.floor(Math.random() * systemLogs.length)]]);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    useFrame(({ clock }) => {
        if (!groupRef.current || isDragging) return;
        const t = clock.elapsedTime + orbitOffset.current;
        // Orbit path
        groupRef.current.position.x = Math.sin(t * 0.5) * 2;
        groupRef.current.position.z = Math.cos(t * 0.5) * 2;
        groupRef.current.position.y = Math.sin(t * 1.5) * 0.2;
        // Face the camera
        groupRef.current.rotation.y = -t * 0.5;
    });

    const colors = {
        ambient: "border-cyan-500/50 text-cyan-400",
        defense: "border-red-500/50 text-red-400",
        assist: "border-emerald-500/50 text-emerald-400"
    };

    return (
        <group ref={groupRef}>
            <Html
                transform
                distanceFactor={1.8}
                position={[0, 0, 0]}
                onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsDragging(true);
                }}
                onPointerUp={() => setIsDragging(false)}
            >
                <div className={`w-48 h-32 bg-black/80 backdrop-blur-xl border-2 rounded-xl p-3 font-mono text-[7px] transition-all duration-500 shadow-2xl relative select-none cursor-grab active:cursor-grabbing ${colors[mode]} ${isDragging ? "scale-105 border-opacity-100" : "border-opacity-30"}`}>
                    <div className="absolute -top-3 left-4 bg-black px-2 py-0.5 border-x-2 border-t-2 border-current rounded-t-lg text-[5px] font-bold">
                        LIVE_PROCESS_TERMINAL
                    </div>
                    <div className="flex justify-between items-center mb-1 border-b border-current pb-1">
                        <span>SENTIENT_OS [v2.4]</span>
                        <div className="flex gap-1">
                            <div className="w-1 h-1 rounded-full bg-red-500" />
                            <div className="w-1 h-1 rounded-full bg-yellow-500" />
                            <div className="w-1 h-1 rounded-full bg-green-500" />
                        </div>
                    </div>
                    <div className="space-y-0.5 opacity-80">
                        {logs.map((log, i) => (
                            <div key={i} className="flex gap-1">
                                <span className="opacity-50">[{new Date().toLocaleTimeString().split(' ')[0]}]</span>
                                <span>{log}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2 h-0.5 bg-current w-1/2 animate-pulse" />
                </div>
            </Html>
        </group>
    );
};

const Hologram = ({ isHovered, mode }) => {
    const textRef = useRef();
    const modeInfo = {
        ambient: { text: "SYSTEM_IDLE", color: "#22d3ee" },
        defense: { text: "SECURITY_ACTIVE", color: "#ef4444" },
        assist: { text: "NEURAL_LINK_SYNC", color: "#10b981" }
    };

    useFrame(({ clock }) => {
        if (!textRef.current) return;
        textRef.current.position.y = 1.8 + Math.sin(clock.elapsedTime * 2) * 0.1;
        textRef.current.rotation.y = Math.sin(clock.elapsedTime * 0.5) * 0.2;
    });

    return (
        <group visible={isHovered}>
            <mesh ref={textRef} position={[0, 1.8, 0.5]}>
                <boxGeometry args={[1.5, 0.05, 0.05]} />
                <meshBasicMaterial color={modeInfo[mode].color} transparent opacity={0.4} />
                {/* Visual representation of a loading bar or code snippet */}
                <group position={[0, -0.15, 0]}>
                    <mesh>
                        <boxGeometry args={[1.2, 0.02, 0.02]} />
                        <meshBasicMaterial color={modeInfo[mode].color} transparent opacity={0.2} />
                    </mesh>
                </group>
            </mesh>
        </group>
    );
};

export function HeroRobot({ mouseX = 0, mouseY = 0, scrollProgress, isHovered = false, disableScrollOffset = false, forceLookLeft = false, mode = "ambient" }) {
    const groupRef = useRef();
    const headRef = useRef();
    const leftArmRef = useRef();
    const rightArmRef = useRef();
    const leftEyeRef = useRef();
    const rightEyeRef = useRef();
    const antennaTopRef = useRef();
    const chestCoreRef = useRef();
    const laserRef = useRef();
    const leftLegRef = useRef();
    const rightLegRef = useRef();
    const leftPupilRef = useRef();
    const rightPupilRef = useRef();
    const particlesRef = useRef();

    // ── Materials ──────────────────────────────────────────────────────────────
    const mat = useMemo(() => ({
        body: new THREE.MeshStandardMaterial({
            color: "#0f0f1a",
            metalness: 0.9,
            roughness: 0.1,
        }),
        bodyAccent: new THREE.MeshStandardMaterial({
            color: "#1a1a3e",
            metalness: 0.95,
            roughness: 0.05,
        }),
        indigo: new THREE.MeshStandardMaterial({
            color: "#4f46e5",
            metalness: 0.8,
            roughness: 0.2,
            emissive: "#3730a3",
            emissiveIntensity: 0.5,
        }),
        cyan: new THREE.MeshStandardMaterial({
            color: "#06b6d4",
            emissive: "#0891b2",
            emissiveIntensity: 2.5,
            metalness: 0.1,
            roughness: 0.1,
        }),
        purple: new THREE.MeshStandardMaterial({
            color: "#a855f7",
            metalness: 0.9,
            roughness: 0.1,
            emissive: "#7e22ce",
            emissiveIntensity: 0.8,
        }),
        pink: new THREE.MeshStandardMaterial({
            color: "#ec4899",
            emissive: "#db2777",
            emissiveIntensity: 1.8,
        }),
        glass: new THREE.MeshStandardMaterial({
            color: "#4f46e5",
            metalness: 0.1,
            roughness: 0.05,
            transparent: true,
            opacity: 0.6,
            emissive: "#4338ca",
            emissiveIntensity: 0.8,
        }),
        joint: new THREE.MeshStandardMaterial({
            color: "#111133",
            metalness: 0.99,
            roughness: 0.01,
        }),
        white: new THREE.MeshStandardMaterial({
            color: "#e0e7ff",
            metalness: 0.7,
            roughness: 0.2,
        }),
        defense: new THREE.MeshStandardMaterial({
            color: "#ef4444",
            emissive: "#ef4444",
            emissiveIntensity: 3,
        }),
        assist: new THREE.MeshStandardMaterial({
            color: "#10b981",
            emissive: "#10b981",
            emissiveIntensity: 3,
        }),
    }), []);

    const eyeMaterial = useMemo(() => {
        if (mode === "defense") return mat.defense;
        if (mode === "assist") return mat.assist;
        return mat.cyan;
    }, [mode, mat]);

    // Particle geometry for background floating dots
    const particleGeo = useMemo(() => {
        const geo = new THREE.BufferGeometry();
        const count = 60;
        const positions = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            positions[i * 3 + 0] = (Math.random() - 0.5) * 8;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 8;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 4 - 1;
        }
        geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        return geo;
    }, []);

    const particleMat = useMemo(() => new THREE.PointsMaterial({
        color: "#6366f1",
        size: 0.03,
        transparent: true,
        opacity: 0.6,
        sizeAttenuation: true,
    }), []);

    useFrame((state) => {
        const t = state.clock.elapsedTime;
        const scroll = scrollProgress ? scrollProgress.get() : 0;

        // Root group: float + follow mouse + scroll interaction
        if (groupRef.current) {
            // Scroll effects: scale down and move as we scroll
            const s = 0.85 - (disableScrollOffset ? 0 : scroll * 0.4);
            groupRef.current.scale.set(s, s, s);

            // Organic Bobbing (Multiple frequencies for a "flying drone" life-like feel)
            const bob = Math.sin(t * 0.8) * 0.12 + Math.sin(t * 1.5) * 0.05 + Math.cos(t * 0.5) * 0.03;

            // Spatial flight on scroll
            const scrollX = disableScrollOffset ? 0 : scroll * 12;
            const scrollY = disableScrollOffset ? 0 : scroll * 15;

            groupRef.current.position.set(
                (disableScrollOffset ? 0 : scrollX),
                bob - (disableScrollOffset ? 0 : scrollY),
                0
            );

            // Body follows mouse (Leaning / Rotation)
            // When forceLookLeft is true, we want to face the center (left) despite the scroll rotation
            const baseScrollRot = scroll * Math.PI * 0.4;
            const mouseFactorY = forceLookLeft ? (-0.8 - baseScrollRot) : mouseX * 0.6;
            const targetRotY = mouseFactorY + Math.sin(t * 0.3) * 0.05 + baseScrollRot;
            const targetRotX = (forceLookLeft ? 0.1 : mouseY * 0.25);

            groupRef.current.rotation.y = THREE.MathUtils.lerp(groupRef.current.rotation.y, targetRotY, 0.11);
            groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, targetRotX, 0.11);
            groupRef.current.rotation.z = scroll * 0.25;

            // Fade out as we scroll away if in hero
            if (!disableScrollOffset) {
                const opacity = 1 - Math.min(scroll * 10, 1);
                groupRef.current.traverse((child) => {
                    if (child.isMesh && child.material) {
                        child.material.transparent = true;
                        child.material.opacity = opacity;
                    }
                });
            }
        }

        // Head tracks mouse + scroll tilt + hover look
        if (headRef.current) {
            const headMouseFactorY = forceLookLeft ? -1.2 : mouseX * 0.8;
            const headTargetRotY = headMouseFactorY + (isHovered ? Math.sin(t * 2) * 0.1 : 0);
            const headTargetRotX = (forceLookLeft ? 0.1 : -mouseY * 0.4) + Math.sin(t * 0.6) * 0.05 + scroll * 0.4;

            headRef.current.rotation.y = THREE.MathUtils.lerp(headRef.current.rotation.y, headTargetRotY, 0.15);
            headRef.current.rotation.x = THREE.MathUtils.lerp(headRef.current.rotation.x, headTargetRotX, 0.15);
        }

        // Arms swing + dynamic scroll lift + Wave on hover
        if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(t * 1.2) * 0.15 - scroll * 0.8;
            leftArmRef.current.rotation.z = 0.3 + Math.sin(t * 0.9) * 0.05 + scroll * 0.3;
        }
        if (rightArmRef.current) {
            if (isHovered) {
                // Happy wave
                rightArmRef.current.rotation.z = -1.2 + Math.sin(t * 8) * 0.3;
                rightArmRef.current.rotation.x = -0.5;
            } else {
                rightArmRef.current.rotation.x = Math.sin(t * 1.2 + Math.PI) * 0.15 - scroll * 0.8;
                rightArmRef.current.rotation.z = -0.3 - Math.sin(t * 0.9) * 0.05 - scroll * 0.3;
            }
        }

        // Legs breathe slightly
        if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(t * 0.6) * 0.05;
        }
        if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(t * 0.6 + Math.PI) * 0.05;
        }

        // Eyes pulse on hover
        if (leftEyeRef.current && rightEyeRef.current) {
            const eyePulse = isHovered ? (1 + Math.sin(t * 10) * 0.2) : 1;
            leftEyeRef.current.scale.set(eyePulse, eyePulse, eyePulse);
            rightEyeRef.current.scale.set(eyePulse, eyePulse, eyePulse);
        }

        // Antenna pulse
        if (antennaTopRef.current) {
            antennaTopRef.current.material.emissiveIntensity =
                (isHovered ? 3.0 : 1.0) + Math.sin(t * (isHovered ? 12 : 5)) * 0.8;
            const scale = 1 + Math.sin(t * (isHovered ? 12 : 5)) * 0.15;
            antennaTopRef.current.scale.set(scale, scale, scale);
        }

        // Chest core pulse
        if (chestCoreRef.current) {
            chestCoreRef.current.rotation.z = t * (isHovered ? 4 : 1.5);
            chestCoreRef.current.material.emissiveIntensity =
                (isHovered ? 3 : 1.5) + Math.sin(t * 3) * 0.5;
        }

        // Particles drift
        if (particlesRef.current) {
            particlesRef.current.rotation.y = t * 0.05;
            particlesRef.current.rotation.x = t * 0.03;
        }

        // Proximity twitch: If mouse is very close (distance approach 0), add high-frequency jitter
        const dist = Math.sqrt(mouseX * mouseX + mouseY * mouseY);
        if (dist < 0.2 && groupRef.current) {
            groupRef.current.position.x += Math.sin(t * 50) * 0.02;
            groupRef.current.position.y += Math.cos(t * 50) * 0.02;
        }

        // Scanning Laser animation
        if (laserRef.current) {
            laserRef.current.position.y = Math.sin(t * 4) * 1.5;
            laserRef.current.material.color.set(mode === "defense" ? "#ef4444" : "#22d3ee");
        }
    });

    return (
        <>
            {/* Background particles */}
            <points ref={particlesRef} geometry={particleGeo} material={particleMat} />

            {/* ── Lighting ── */}
            <ambientLight intensity={0.4} />
            <directionalLight position={[5, 8, 5]} intensity={1.5} color="#c7d2fe" castShadow />
            <directionalLight position={[-5, 3, -3]} intensity={0.6} color="#a78bfa" />
            <pointLight position={[0, 3, 3]} intensity={3} color={mode === "defense" ? "#ef4444" : (mode === "assist" ? "#10b981" : "#06b6d4")} distance={8} />
            <pointLight position={[2, -2, 2]} intensity={2} color="#a855f7" distance={6} />
            <pointLight position={[-2, 2, 1]} intensity={1.5} color="#4f46e5" distance={5} />

            {/* Holographic Alert */}
            <Hologram isHovered={isHovered} mode={mode} />

            {/* Orbiting Draggable Terminal */}
            <OrbitingTerminal mode={mode} />

            {/* Scanning Laser (Appear on hover) */}
            {isHovered && (
                <group position={[0, 0, 0.5]}>
                    <mesh ref={laserRef}>
                        <boxGeometry args={[3, 0.02, 0.02]} />
                        <meshBasicMaterial color="#22d3ee" transparent opacity={0.6} />
                    </mesh>
                </group>
            )}

            <DataStreams isHovered={isHovered} />

            <group ref={groupRef} position={[0, -0.3, 0]}>

                {/* ── TORSO ── */}
                <group position={[0, 0, 0]}>
                    {/* Main torso box */}
                    <mesh material={mat.body} castShadow>
                        <boxGeometry args={[1.0, 1.1, 0.55]} />
                    </mesh>
                    {/* Torso beveled edge strips */}
                    {[[-0.52, 0, 0], [0.52, 0, 0]].map(([x, y, z], i) => (
                        <mesh key={i} position={[x, y, z]} material={mat.joint}>
                            <boxGeometry args={[0.06, 1.08, 0.53]} />
                        </mesh>
                    ))}
                    {/* Chest plate */}
                    <mesh position={[0, 0.08, 0.29]} material={mat.bodyAccent}>
                        <boxGeometry args={[0.62, 0.55, 0.04]} />
                    </mesh>
                    {/* Chest core (spinning octagon) */}
                    <mesh ref={chestCoreRef} position={[0, 0.15, 0.33]}>
                        <cylinderGeometry args={[0.12, 0.12, 0.04, 8]} />
                        <meshStandardMaterial
                            color="#06b6d4"
                            emissive="#06b6d4"
                            emissiveIntensity={2}
                            metalness={0.1}
                            roughness={0.1}
                        />
                    </mesh>
                    {/* Chest ring */}
                    <mesh position={[0, 0.15, 0.33]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.16, 0.02, 8, 20]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={1} />
                    </mesh>
                    {/* Grid lines on chest */}
                    {[-0.18, 0, 0.18].map((x, i) => (
                        <mesh key={i} position={[x, -0.08, 0.32]} material={mat.indigo}>
                            <boxGeometry args={[0.025, 0.3, 0.01]} />
                        </mesh>
                    ))}
                    {[-0.05, 0.07, 0.19].map((y, i) => (
                        <mesh key={i} position={[0, y - 0.15, 0.32]} material={mat.glass}>
                            <boxGeometry args={[0.5, 0.015, 0.01]} />
                        </mesh>
                    ))}
                    {/* Side vents */}
                    {[1, -1].map((side, i) => (
                        <group key={i}>
                            {[0.15, 0, -0.15].map((yy, j) => (
                                <mesh key={j} position={[side * 0.5, yy, 0]} material={mat.indigo}>
                                    <boxGeometry args={[0.04, 0.04, 0.5]} />
                                </mesh>
                            ))}
                        </group>
                    ))}
                    {/* Waist belt */}
                    <mesh position={[0, -0.55, 0]}>
                        <boxGeometry args={[1.02, 0.1, 0.57]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4338ca" emissiveIntensity={0.5} metalness={0.9} roughness={0.1} />
                    </mesh>
                    <mesh position={[0, -0.55, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.38, 0.025, 6, 40]} />
                        <meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={1.2} />
                    </mesh>
                </group>

                {/* ── HEAD ── */}
                <group ref={headRef} position={[0, 1.05, 0]}>
                    {/* Head box */}
                    <mesh material={mat.body} castShadow>
                        <boxGeometry args={[0.78, 0.7, 0.62]} />
                    </mesh>
                    {/* Ear panels */}
                    {[1, -1].map((side, i) => (
                        <mesh key={i} position={[side * 0.42, 0, 0]} material={mat.joint}>
                            <boxGeometry args={[0.06, 0.5, 0.5]} />
                        </mesh>
                    ))}
                    {/* Visor (cyan glass) */}
                    <mesh position={[0, 0.05, 0.32]} material={mat.glass}>
                        <boxGeometry args={[0.6, 0.28, 0.03]} />
                    </mesh>
                    {/* Eye hardware strips */}
                    <mesh ref={leftEyeRef} position={[-0.14, 0.06, 0.345]} material={eyeMaterial}>
                        <sphereGeometry args={[0.07, 16, 16]} />
                    </mesh>
                    <mesh ref={rightEyeRef} position={[0.14, 0.06, 0.345]} material={eyeMaterial}>
                        <sphereGeometry args={[0.07, 16, 16]} />
                    </mesh>
                    {/* Mouth grill */}
                    {[-0.15, -0.05, 0.05, 0.15].map((x, i) => (
                        <mesh key={i} position={[x, -0.12, 0.32]} material={mat.indigo}>
                            <boxGeometry args={[0.06, 0.025, 0.02]} />
                        </mesh>
                    ))}
                    {/* Jaw line indicator */}
                    <mesh position={[0, -0.2, 0.28]}>
                        <boxGeometry args={[0.5, 0.02, 0.02]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={0.8} />
                    </mesh>
                    {/* Forehead detail */}
                    <mesh position={[0, 0.28, 0.32]}>
                        <boxGeometry args={[0.3, 0.04, 0.02]} />
                        <meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={1} />
                    </mesh>
                    {/* Neck connector */}
                    <mesh position={[0, -0.4, 0]} material={mat.joint}>
                        <cylinderGeometry args={[0.2, 0.26, 0.18, 10]} />
                    </mesh>
                    {/* Antenna stem */}
                    <mesh position={[0, 0.43, 0]}>
                        <cylinderGeometry args={[0.025, 0.025, 0.22, 8]} />
                        <meshStandardMaterial color="#1e1b4b" metalness={0.9} roughness={0.1} />
                    </mesh>
                    {/* Antenna orb */}
                    <mesh ref={antennaTopRef} position={[0, 0.57, 0]}>
                        <sphereGeometry args={[0.065, 12, 12]} />
                        <meshStandardMaterial color="#a855f7" emissive="#a855f7" emissiveIntensity={2} />
                    </mesh>
                    {/* Antenna glow ring */}
                    <mesh position={[0, 0.57, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.1, 0.01, 6, 20]} />
                        <meshStandardMaterial color="#c084fc" emissive="#c084fc" emissiveIntensity={1.5} transparent opacity={0.7} />
                    </mesh>
                </group>

                {/* ── LEFT ARM ── */}
                <group ref={leftArmRef} position={[-0.66, 0.3, 0]}>
                    {/* Shoulder joint */}
                    <mesh material={mat.joint}>
                        <sphereGeometry args={[0.16, 12, 12]} />
                    </mesh>
                    <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.13, 0.02, 6, 20]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={0.8} />
                    </mesh>
                    {/* Upper arm */}
                    <mesh position={[-0.05, -0.28, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.26, 0.5, 0.26]} />
                    </mesh>
                    {/* Arm accent strip */}
                    <mesh position={[-0.05, -0.28, 0.14]} material={mat.indigo}>
                        <boxGeometry args={[0.14, 0.46, 0.02]} />
                    </mesh>
                    {/* Elbow joint */}
                    <mesh position={[-0.05, -0.56, 0]} material={mat.joint}>
                        <sphereGeometry args={[0.12, 10, 10]} />
                    </mesh>
                    {/* Forearm */}
                    <mesh position={[-0.05, -0.8, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.22, 0.45, 0.22]} />
                    </mesh>
                    {/* Wrist ring */}
                    <mesh position={[-0.05, -1.05, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.1, 0.02, 6, 16]} />
                        <meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={1.2} />
                    </mesh>
                    {/* Hand */}
                    <mesh position={[-0.05, -1.2, 0]} material={mat.bodyAccent}>
                        <boxGeometry args={[0.2, 0.18, 0.16]} />
                    </mesh>
                </group>

                {/* ── RIGHT ARM ── */}
                <group ref={rightArmRef} position={[0.66, 0.3, 0]}>
                    <mesh material={mat.joint}>
                        <sphereGeometry args={[0.16, 12, 12]} />
                    </mesh>
                    <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.13, 0.02, 6, 20]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={0.8} />
                    </mesh>
                    <mesh position={[0.05, -0.28, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.26, 0.5, 0.26]} />
                    </mesh>
                    <mesh position={[0.05, -0.28, 0.14]} material={mat.indigo}>
                        <boxGeometry args={[0.14, 0.46, 0.02]} />
                    </mesh>
                    <mesh position={[0.05, -0.56, 0]} material={mat.joint}>
                        <sphereGeometry args={[0.12, 10, 10]} />
                    </mesh>
                    <mesh position={[0.05, -0.8, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.22, 0.45, 0.22]} />
                    </mesh>
                    <mesh position={[0.05, -1.05, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.1, 0.02, 6, 16]} />
                        <meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={1.2} />
                    </mesh>
                    <mesh position={[0.05, -1.2, 0]} material={mat.bodyAccent}>
                        <boxGeometry args={[0.2, 0.18, 0.16]} />
                    </mesh>
                </group>

                {/* ── HIPS ── */}
                <mesh position={[0, -0.7, 0]} material={mat.body}>
                    <boxGeometry args={[0.95, 0.28, 0.55]} />
                </mesh>

                {/* ── LEFT LEG ── */}
                <group ref={leftLegRef} position={[-0.26, -1.14, 0]}>
                    {/* Hip joint */}
                    <mesh material={mat.joint}>
                        <sphereGeometry args={[0.14, 10, 10]} />
                    </mesh>
                    {/* Thigh */}
                    <mesh position={[0, -0.32, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.32, 0.52, 0.32]} />
                    </mesh>
                    <mesh position={[0, -0.32, 0.17]} material={mat.indigo}>
                        <boxGeometry args={[0.18, 0.48, 0.02]} />
                    </mesh>
                    {/* Knee */}
                    <mesh position={[0, -0.62, 0]} material={mat.joint}>
                        <sphereGeometry args={[0.13, 10, 10]} />
                    </mesh>
                    <mesh position={[0, -0.62, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.1, 0.015, 6, 16]} />
                        <meshStandardMaterial color="#a855f7" emissive="#a855f7" emissiveIntensity={1} />
                    </mesh>
                    {/* Shin */}
                    <mesh position={[0, -0.96, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.26, 0.55, 0.28]} />
                    </mesh>
                    {/* Ankle */}
                    <mesh position={[0, -1.27, 0]} material={mat.joint}>
                        <sphereGeometry args={[0.1, 10, 10]} />
                    </mesh>
                    {/* Boot */}
                    <mesh position={[0, -1.44, 0.07]} material={mat.bodyAccent}>
                        <boxGeometry args={[0.3, 0.18, 0.42]} />
                    </mesh>
                    {/* Boot sole glow */}
                    <mesh position={[0, -1.54, 0.07]}>
                        <boxGeometry args={[0.28, 0.02, 0.4]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={1.5} />
                    </mesh>
                </group>

                {/* ── RIGHT LEG ── */}
                <group ref={rightLegRef} position={[0.26, -1.14, 0]}>
                    <mesh material={mat.joint}>
                        <sphereGeometry args={[0.14, 10, 10]} />
                    </mesh>
                    <mesh position={[0, -0.32, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.32, 0.52, 0.32]} />
                    </mesh>
                    <mesh position={[0, -0.32, 0.17]} material={mat.indigo}>
                        <boxGeometry args={[0.18, 0.48, 0.02]} />
                    </mesh>
                    <mesh position={[0, -0.62, 0]} material={mat.joint}>
                        <sphereGeometry args={[0.13, 10, 10]} />
                    </mesh>
                    <mesh position={[0, -0.62, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.1, 0.015, 6, 16]} />
                        <meshStandardMaterial color="#a855f7" emissive="#a855f7" emissiveIntensity={1} />
                    </mesh>
                    {/* Shin */}
                    <mesh position={[0, -0.96, 0]} material={mat.body} castShadow>
                        <boxGeometry args={[0.26, 0.55, 0.28]} />
                    </mesh>
                    {/* Ankle */}
                    <mesh position={[0, -1.27, 0]} material={mat.joint}>
                        <sphereGeometry args={[0.1, 10, 10]} />
                    </mesh>
                    {/* Boot */}
                    <mesh position={[0, -1.44, 0.07]} material={mat.bodyAccent}>
                        <boxGeometry args={[0.3, 0.18, 0.42]} />
                    </mesh>
                    {/* Boot sole glow */}
                    <mesh position={[0, -1.54, 0.07]}>
                        <boxGeometry args={[0.28, 0.02, 0.4]} />
                        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={1.5} />
                    </mesh>
                </group>

                {/* ── ENERGY ORBS ── floating around robot */}
                {[
                    { pos: [-1.5, 0.8, 0.5], color: "#06b6d4", speed: 1.2 },
                    { pos: [1.4, -0.3, 0.6], color: "#a855f7", speed: 0.9 },
                    { pos: [0.8, 1.2, -0.4], color: "#4f46e5", speed: 1.5 },
                    { pos: [-1.0, -0.8, 0.3], color: "#ec4899", speed: 0.7 },
                ].map(({ pos, color, speed }, i) => (
                    <OrbitalDot key={i} basePos={pos} color={color} speed={speed} index={i} />
                ))}
            </group>
        </>
    );
}

function OrbitalDot({ basePos, color, speed, index }) {
    const ref = useRef();
    useFrame(({ clock }) => {
        if (!ref.current) return;
        const t = clock.elapsedTime * speed + index * 1.5;
        ref.current.position.x = basePos[0] + Math.sin(t) * 0.2;
        ref.current.position.y = basePos[1] + Math.cos(t * 0.7) * 0.15;
        ref.current.position.z = basePos[2] + Math.sin(t * 1.3) * 0.1;
        const scale = 1 + Math.sin(t * 3) * 0.2;
        ref.current.scale.set(scale, scale, scale);
    });
    return (
        <mesh ref={ref} position={basePos}>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={3} />
        </mesh>
    );
}

export default HeroRobot;
